import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { getBasePath, getCoursesPath } from '../utils/navigationHelper';
import LessonViewsModal from '../components/LessonViewsModal';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  EyeIcon,
  DocumentIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  LinkIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const LessonManagementPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, text, video, audio, document, link
  const [filterStudentType, setFilterStudentType] = useState('all'); // all, shs1, shs2, shs3
  const [filterStatus, setFilterStatus] = useState('all'); // all, published, draft, pinned
  const [viewsModalOpen, setViewsModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);

  useEffect(() => {
    fetchCourseAndLessons();
  }, [courseId]);

  const fetchCourseAndLessons = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching lessons for courseId:', courseId);
      console.log('👤 Current user:', user);
      console.log('📍 Base path:', getBasePath(location, user));
      
      // Fetch course details
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      console.log('✅ Course fetched:', courseRes.data);

      // Fetch lessons
      const lessonsRes = await api.get(`/lessons/course/${courseId}`);
      console.log('✅ Lessons API response:', lessonsRes.data);
      console.log('📊 Number of lessons:', lessonsRes.data.length);
      setLessons(lessonsRes.data);
    } catch (err) {
      console.error('❌ Error fetching data:', err);
      console.error('❌ Error response:', err.response);
      setError(err.response?.data?.error || 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;

    try {
      await api.delete(`/lessons/${lessonId}`);
      setLessons(lessons.filter(l => l.id !== lessonId));
      alert('Lesson deleted successfully');
    } catch (err) {
      console.error('Error deleting lesson:', err);
      alert(err.response?.data?.error || 'Failed to delete lesson');
    }
  };

  const handleTogglePublish = async (lessonId, currentPublishedAt) => {
    try {
      const isPublished = !!currentPublishedAt;
      const action = isPublished ? 'unpublish' : 'publish';
      
      // Update lesson with publishedAt field
      await api.put(`/lessons/${lessonId}`, {
        publishedAt: isPublished ? null : new Date().toISOString()
      });

      // Update local state
      setLessons(lessons.map(lesson => 
        lesson.id === lessonId 
          ? { ...lesson, publishedAt: isPublished ? null : new Date().toISOString() }
          : lesson
      ));

      alert(`Lesson ${action}ed successfully`);
    } catch (err) {
      console.error('Error toggling publish status:', err);
      alert(err.response?.data?.error || 'Failed to update lesson');
    }
  };

  const handleViewLesson = async (lessonId) => {
    // Mark lesson as viewed when teacher clicks to view it
    try {
      await api.post(`/lessons/${lessonId}/mark-viewed`);
      // Update local state
      setLessons(lessons.map(lesson => 
        lesson.id === lessonId 
          ? { ...lesson, viewedAt: new Date().toISOString() }
          : lesson
      ));
    } catch (err) {
      console.error('Error marking lesson as viewed:', err);
      // Non-critical error, don't show to user
    }
  };

  const handleViewStats = (lesson) => {
    setSelectedLesson(lesson);
    setViewsModalOpen(true);
  };

  // Helper: get lesson status badge (NEW or UPDATED)
  const getLessonBadge = (lesson) => {
    // Don't show badge if teacher has already viewed this lesson
    if (lesson.viewedAt) {
      return null;
    }

    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000)); // 3 days
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days
    
    const publishedDate = new Date(lesson.publishedAt);
    const updatedDate = new Date(lesson.updatedAt);
    const createdDate = new Date(lesson.createdAt);
    
    // Check if lesson is new (published in last 7 days)
    if (lesson.publishedAt && publishedDate > sevenDaysAgo) {
      return { type: 'NEW', color: 'green' };
    }
    
    // Check if lesson is recently updated (updated in last 3 days)
    // Only show UPDATED if:
    // 1. Published more than 7 days ago (no longer "new")
    // 2. Updated in last 3 days
    // 3. Updated significantly after creation (more than 1 day difference)
    if (lesson.updatedAt && 
        lesson.publishedAt &&
        publishedDate <= sevenDaysAgo && // No longer "new"
        updatedDate > threeDaysAgo && 
        (updatedDate - createdDate) > (24 * 60 * 60 * 1000)) { // More than 1 day after creation
      return { type: 'UPDATED', color: 'blue' };
    }
    
    return null;
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return <VideoCameraIcon className="h-5 w-5 text-purple-600" />;
      case 'audio':
        return <MusicalNoteIcon className="h-5 w-5 text-pink-600" />;
      case 'document':
        return <DocumentIcon className="h-5 w-5 text-blue-600" />;
      case 'link':
        return <LinkIcon className="h-5 w-5 text-green-600" />;
      case 'text':
      default:
        return <ChatBubbleLeftIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStudentTypeLabel = (studentType) => {
    if (!studentType) return 'All Students';
    return studentType.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lessons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(getCoursesPath(location, user))}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to Courses
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {course?.title || 'Course Lessons'}
            </h1>
            <p className="text-gray-600 mt-2">{course?.description}</p>
            <p className="text-sm text-gray-500 mt-1">
              {lessons.length} lesson{lessons.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => navigate(`${getBasePath(location, user)}/courses/${courseId}/lessons/create`)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            Create Lesson
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="lg:col-span-2 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search lessons by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Content Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="text">Text</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="document">Document</option>
            <option value="link">Link</option>
            <option value="image">Image</option>
            <option value="multiple">Multiple Content</option>
          </select>

          {/* Student Type Filter */}
          <select
            value={filterStudentType}
            onChange={(e) => setFilterStudentType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Students</option>
            <option value="shs1">SHS1</option>
            <option value="shs2">SHS2</option>
            <option value="shs3">SHS3</option>
          </select>
        </div>

        {/* Status Filter Row */}
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filterStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('published')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filterStatus === 'published'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Published
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filterStatus === 'draft'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Draft
          </button>
          <button
            onClick={() => setFilterStatus('pinned')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filterStatus === 'pinned'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pinned
          </button>
        </div>

        {/* Results Counter */}
        {(searchQuery || filterType !== 'all' || filterStudentType !== 'all' || filterStatus !== 'all') && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {lessons.filter(lesson => {
              const query = searchQuery.toLowerCase();
              const matchesSearch = (
                lesson.title.toLowerCase().includes(query) ||
                (lesson.description && lesson.description.toLowerCase().includes(query))
              );
              const matchesType = filterType === 'all' || lesson.contentType === filterType;
              const matchesStudentType = filterStudentType === 'all' || 
                (filterStudentType === 'all' && !lesson.studentType) ||
                lesson.studentType === filterStudentType;
              const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'published' && lesson.publishedAt) ||
                (filterStatus === 'draft' && !lesson.publishedAt) ||
                (filterStatus === 'pinned' && lesson.isPinned);
              return matchesSearch && matchesType && matchesStudentType && matchesStatus;
            }).length} of {lessons.length} lessons
            {(searchQuery || filterType !== 'all' || filterStudentType !== 'all' || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setFilterStudentType('all');
                  setFilterStatus('all');
                }}
                className="ml-3 text-blue-600 hover:text-blue-800 underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Lessons List */}
      {lessons.filter(lesson => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          lesson.title.toLowerCase().includes(query) ||
          (lesson.description && lesson.description.toLowerCase().includes(query))
        );
        const matchesType = filterType === 'all' || lesson.contentType === filterType;
        const matchesStudentType = filterStudentType === 'all' || 
          (filterStudentType === 'all' && !lesson.studentType) ||
          lesson.studentType === filterStudentType;
        const matchesStatus = filterStatus === 'all' ||
          (filterStatus === 'published' && lesson.publishedAt) ||
          (filterStatus === 'draft' && !lesson.publishedAt) ||
          (filterStatus === 'pinned' && lesson.isPinned);
        return matchesSearch && matchesType && matchesStudentType && matchesStatus;
      }).length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ChatBubbleLeftIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-2">
            {lessons.length === 0 ? 'No lessons yet' : 'No lessons match your filters'}
          </p>
          <p className="text-gray-500 mb-4">
            {lessons.length === 0 
              ? 'Create your first lesson to get started' 
              : 'Try adjusting your search or filter criteria'
            }
          </p>
          {lessons.length === 0 ? (
            <button
              onClick={() => navigate(`${getBasePath(location, user)}/courses/${courseId}/lessons/create`)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Create First Lesson
            </button>
          ) : (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterType('all');
                setFilterStudentType('all');
                setFilterStatus('all');
              }}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {lessons.filter(lesson => {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
              lesson.title.toLowerCase().includes(query) ||
              (lesson.description && lesson.description.toLowerCase().includes(query))
            );
            const matchesType = filterType === 'all' || lesson.contentType === filterType;
            const matchesStudentType = filterStudentType === 'all' || 
              (filterStudentType === 'all' && !lesson.studentType) ||
              lesson.studentType === filterStudentType;
            const matchesStatus = filterStatus === 'all' ||
              (filterStatus === 'published' && lesson.publishedAt) ||
              (filterStatus === 'draft' && !lesson.publishedAt) ||
              (filterStatus === 'pinned' && lesson.isPinned);
            return matchesSearch && matchesType && matchesStudentType && matchesStatus;
          }).map((lesson) => (
            <div
              key={lesson.id}
              className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                lesson.isPinned ? 'border-yellow-400' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Lesson Header */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {getContentIcon(lesson.contentType)}
                    <h3 className="text-xl font-semibold text-gray-900">
                      {lesson.title}
                    </h3>
                    {lesson.isPinned && (
                      <MapPinIcon className="h-5 w-5 text-yellow-500" />
                    )}
                    {(() => {
                      const badge = getLessonBadge(lesson);
                      return badge ? (
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          badge.color === 'green' 
                            ? 'bg-green-100 text-green-700 border border-green-300 animate-pulse' 
                            : 'bg-blue-100 text-blue-700 border border-blue-300'
                        }`}>
                          {badge.type}
                        </span>
                      ) : null;
                    })()}
                  </div>

                  {/* Lesson Description */}
                  {lesson.description && (
                    <p className="text-gray-600 mb-3">{lesson.description}</p>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <AcademicCapIcon className="h-4 w-4" />
                      {getStudentTypeLabel(lesson.studentType)}
                    </span>
                    <span>
                      Type: <span className="font-medium capitalize">{lesson.contentType}</span>
                    </span>
                    <span>
                      {lesson.publishedAt 
                        ? `Published: ${new Date(lesson.publishedAt).toLocaleDateString()}`
                        : <span className="text-orange-600 font-medium">Draft</span>
                      }
                    </span>
                    {lesson.viewCount !== undefined && (
                      <span className="flex items-center gap-1 text-blue-600 font-medium">
                        <EyeIcon className="h-4 w-4" />
                        {lesson.viewCount} view{lesson.viewCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {lesson.quizzes && lesson.quizzes.length > 0 && (
                      <span className="text-blue-600 font-medium">
                        {lesson.quizzes.length} quiz{lesson.quizzes.length !== 1 ? 'zes' : ''} attached
                      </span>
                    )}
                  </div>

                  {/* Attached Quizzes */}
                  {lesson.quizzes && lesson.quizzes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Attached Quizzes:</p>
                      <div className="space-y-1">
                        {lesson.quizzes.map((quiz) => (
                          <div key={quiz.id} className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                            <span>{quiz.title}</span>
                            {quiz.lessonQuizMeta?.isRequired && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                Required
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 ml-4">
                  {lesson.viewCount !== undefined && lesson.viewCount > 0 && (
                    <button
                      onClick={() => handleViewStats(lesson)}
                      className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded"
                      title="View Statistics"
                    >
                      <ChartBarIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleTogglePublish(lesson.id, lesson.publishedAt)}
                    className={`p-2 rounded ${
                      lesson.publishedAt 
                        ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                        : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                    }`}
                    title={lesson.publishedAt ? 'Published - Click to unpublish' : 'Draft - Click to publish'}
                  >
                    {lesson.publishedAt ? (
                      <CheckCircleIcon className="h-5 w-5" />
                    ) : (
                      <XCircleIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      handleViewLesson(lesson.id);
                      navigate(`${getBasePath(location, user)}/courses/${courseId}/lessons/${lesson.id}/view`);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="View"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => navigate(`${getBasePath(location, user)}/courses/${courseId}/lessons/${lesson.id}/edit`)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(lesson.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lesson Views Modal */}
      <LessonViewsModal
        lessonId={selectedLesson?.id}
        lessonTitle={selectedLesson?.title}
        isOpen={viewsModalOpen}
        onClose={() => {
          setViewsModalOpen(false);
          setSelectedLesson(null);
        }}
      />
    </div>
  );
};

export default LessonManagementPage;
